import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type Sender = "vozis" | "lead" | "stage";
interface Message { id: number; sender: Sender; text: string; }
interface Option { label: string; type?: "positive" | "objection"; action: () => void; }
interface LeadState { teamSize: string | null; pain: string | null; competitor: string | null; status: "D1" | "D3" | null; }

let msgCounter = 0;
const nextId = () => ++msgCounter;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
function getNextWeekISO() {
  const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(10, 0, 0, 0);
  return d.toISOString().slice(0, 16).replace("T", " ");
}

// ─── PROTOTYPE (English) ──────────────────────────────────────────────────────
function VozisPrototypeEN() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [timerSec, setTimerSec] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [endData, setEndData] = useState<{ status: "D1" | "D3"; state: LeadState } | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const leadState = useRef<LeadState>({ teamSize: null, pain: null, competitor: null, status: null });

  useEffect(() => {
    if (!timerActive) return;
    const iv = setInterval(() => setTimerSec((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [timerActive]);

  const timerLabel = `${String(Math.floor(timerSec / 60)).padStart(2, "0")}:${String(timerSec % 60).padStart(2, "0")}`;

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping]);

  const addMsg = (sender: Sender, text: string) =>
    setMessages((prev) => [...prev, { id: nextId(), sender, text }]);
  const vozis = async (text: string, ms = 1200) => { setOptions([]); setIsTyping(true); await sleep(ms); setIsTyping(false); addMsg("vozis", text); };
  const lead = async (text: string) => { setOptions([]); addMsg("lead", text); await sleep(350); };
  const stage = (label: string) => addMsg("stage", label);

  const startFlow = async () => {
    setTimerActive(true); await sleep(400); stage("greeting");
    await vozis("Hi <b>John</b>! Can you hear me okay?", 700);
    setOptions([
      { label: "Yes, loud and clear.", type: "positive", action: opening },
      { label: "Who's this?", action: async () => { await lead("Who's this?"); await vozis("This is Vozis, an AI sales assistant. Do you have a minute to chat?", 800); setOptions([{ label: "Sure, go ahead.", type: "positive", action: opening }, { label: "Not right now.", action: noTime }]); } },
    ]);
  };

  const opening = async () => {
    await lead("Sure, go ahead."); stage("opening");
    await vozis("I'm calling from Vozis. I have something that could really help your sales team. Is now a good time?", 1000);
    setOptions([{ label: "Yes, tell me more.", type: "positive", action: pitch }, { label: "Not right now.", action: noTime }]);
  };

  const noTime = async () => {
    await lead("I'm pretty busy right now.");
    await vozis("I completely understand, John. What if we grabbed 20 minutes next week? No commitment at all.", 1000);
    setOptions([{ label: "Ok, keep it quick.", type: "positive", action: pitch }, { label: "Send me an email.", action: async () => { await lead("Just send me an email."); await vozis("Of course! But honestly, the material alone won't show what Vozis does differently. It's like seeing a photo of food vs actually tasting it. What if I schedule 20 minutes to show you live?", 1200); setOptions([{ label: "Alright, sounds fair.", type: "positive", action: pitch }, { label: "No thanks.", action: closeNegative }]); } }]);
  };

  const pitch = async () => {
    await lead("Ok, go ahead."); stage("product pitch");
    await vozis("Vozis automates sales calls with AI — prospecting, follow-up, lead qualification — all with the naturalness of a human conversation. Have you heard of anything like this before?", 1300);
    setOptions([
      { label: "Never heard of it.", action: async () => { await lead("Never heard of it."); await vozis("Interesting! John, imagine your team focused <b>only on closing hot leads</b> while Vozis handles all the outreach volume. Does that fit your reality?", 1100); showFit(); } },
      { label: "I've seen similar tools.", action: async () => { await lead("I've seen something similar."); await vozis("Great that you're familiar with the concept! John, imagine your team focused <b>only on closing</b> while Vozis handles all the volume. Does that fit?", 1000); showFit(); } },
    ]);
  };

  const showFit = () => setOptions([
    { label: "Yes, that makes a lot of sense!", type: "positive", action: qualifyTeam },
    { label: "We already use another tool.", action: async () => { await lead("We already use another automation tool."); await vozis("That's great that you're thinking about automation! Which tool are you using?", 800); setOptions([{ label: "HubSpot.", action: async () => { leadState.current.competitor = "HubSpot"; await lead("HubSpot."); await vozis("Many of our clients came from HubSpot. Vozis differentiates itself through <b>conversational naturalness</b> and active listening AI. Want a quick comparison?", 1200); setOptions([{ label: "Yes, go ahead.", type: "positive", action: qualifyTeam }, { label: "No, we're happy with it.", action: closeNegative }]); } }, { label: "Salesforce.", action: async () => { leadState.current.competitor = "Salesforce"; await lead("Salesforce."); await vozis("Several clients came from Salesforce. The difference is in the <b>call naturalness</b> — Vozis sounds genuinely human. Worth a quick comparison?", 1200); setOptions([{ label: "Yes, let's hear it.", type: "positive", action: qualifyTeam }, { label: "No thanks.", action: closeNegative }]); } }]); } },
    { label: "Our team handles it fine.", action: async () => { await lead("Our team already handles it fine."); await vozis("That's great! But here's the question: <em>what if you could do 3x the outreach without hiring anyone?</em> Vozis doesn't replace your team — it amplifies it. Your reps focus on closing, the AI handles the rest. Worth knowing about, right?", 1400); setOptions([{ label: "That's a fair point.", type: "positive", action: qualifyTeam }, { label: "No thanks.", action: closeNegative }]); } },
  ]);

  const qualifyTeam = async () => {
    await lead("Yes, makes sense."); stage("qualification");
    await vozis("Perfect, John! Quick question — how many people are on your sales team right now?", 900);
    setOptions([
      { label: "2 to 5 people", action: async () => { leadState.current.teamSize = "2-5 people"; await lead("About 5 people."); await qualifyPain(); } },
      { label: "6 to 15 people", action: async () => { leadState.current.teamSize = "6-15 people"; await lead("Around 10 reps."); await qualifyPain(); } },
      { label: "More than 15", action: async () => { leadState.current.teamSize = "15+ people"; await lead("More than 15."); await qualifyPain(); } },
    ]);
  };

  const qualifyPain = async () => {
    await vozis("Got it! And right now, would you say your biggest challenge is <b>lack of outreach scale</b>, <b>too many leads to call</b>, or <b>poor lead quality</b> reaching your closers?", 1000);
    setOptions([
      { label: "Lack of scale.", action: async () => { leadState.current.pain = "lack of scale"; await lead("Lack of scale, mostly."); await qualifyCompetitor(); } },
      { label: "Too many leads to call.", action: async () => { leadState.current.pain = "too many leads"; await lead("Volume is the issue."); await qualifyCompetitor(); } },
      { label: "Poor quality leads.", action: async () => { leadState.current.pain = "lead quality"; await lead("Lead quality is poor."); await qualifyCompetitor(); } },
    ]);
  };

  const qualifyCompetitor = async () => {
    await vozis("That makes sense. Have you ever tried any automation tool similar to Vozis before?", 900);
    setOptions([
      { label: "No, never.", action: async () => { if (!leadState.current.competitor) leadState.current.competitor = "none"; await lead("No, never tried."); await nextSteps(); } },
      { label: "Yes, but it didn't work well.", action: async () => { await lead("Tried something once — didn't perform."); leadState.current.competitor = "previous tool"; await nextSteps(); } },
    ]);
  };

  const nextSteps = async () => {
    stage("next steps");
    await vozis("Understood! Based on what you've shared, I think it makes a lot of sense to show you the platform live and build a tailored strategy for your scenario. Can I schedule a <b>20-minute chat</b> with one of our specialists?", 1300);
    setOptions([
      { label: "Yes, let's do it!", type: "positive", action: bookMeeting },
      { label: "What's the pricing?", action: async () => { await lead("What's the pricing?"); await vozis("Great question! Investment varies depending on your needs — we have a starter plan and one for scaling. The specialist walks you through both in the meeting. Deal?", 1200); setOptions([{ label: "Sure, let's book it.", type: "positive", action: bookMeeting }, { label: "I'd rather know the price first.", action: closeNegative }]); } },
      { label: "Not interested right now.", action: async () => { await lead("Not really interested at the moment."); await vozis("I completely understand, John. But companies in your sector are cutting <b>30% of repetitive task time</b> and growing qualified contacts by <b>40%</b>. Wouldn't 20 minutes be worth it? Zero commitment.", 1300); setOptions([{ label: "Ok, 20 minutes is fine.", type: "positive", action: bookMeeting }, { label: "No thanks, really.", action: closeNegative }]); } },
    ]);
  };

  const bookMeeting = async () => {
    leadState.current.status = "D1"; await lead("Sure, go ahead and book it."); stage("closing — positive");
    await vozis("Thank you so much for your time, John! The calendar invite is already in your inbox. Have a great day. Talk soon!", 900);
    setTimerActive(false); await sleep(700); setEndData({ status: "D1", state: { ...leadState.current } });
  };

  const closeNegative = async () => {
    leadState.current.status = "D3"; await lead("No thank you.");
    await vozis("No problem at all, John! We're here if you ever need us. Have a great day. Bye!", 800);
    setTimerActive(false); await sleep(700); setEndData({ status: "D3", state: { ...leadState.current } });
  };

  const restart = () => {
    msgCounter = 0; setMessages([]); setOptions([]); setIsTyping(false);
    setTimerSec(0); setTimerActive(false); setEndData(null);
    leadState.current = { teamSize: null, pain: null, competitor: null, status: null };
  };

  return (
    <div className="bg-card rounded-3xl overflow-hidden border border-border shadow-premium w-full max-w-sm mx-auto">
      <div className="bg-primary/20 px-5 py-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-primary/30 flex items-center justify-center text-primary font-semibold text-lg flex-shrink-0">V</div>
        <div><p className="text-foreground font-medium text-sm leading-tight">Vozis AI</p><p className="text-primary text-xs mt-0.5">Active prospecting assistant</p></div>
      </div>
      <div className="bg-primary/10 px-5 py-1.5 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-primary animate-pulse flex-shrink-0" />
        <span className="text-primary text-xs font-mono">{timerLabel}</span>
        <span className="ml-auto text-muted-foreground text-xs">on call</span>
      </div>
      <div ref={chatRef} className="h-72 overflow-y-auto px-4 py-3 flex flex-col gap-2.5 bg-background scroll-smooth">
        {messages.map((msg) => {
          if (msg.sender === "stage") return <div key={msg.id} className="self-center"><span className="text-xs text-muted-foreground bg-card border border-border px-3 py-1 rounded-full">{msg.text}</span></div>;
          if (msg.sender === "vozis") return <div key={msg.id} className="self-start max-w-[82%] bg-primary/15 text-foreground text-xs px-3.5 py-2.5 rounded-2xl rounded-bl-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: msg.text }} />;
          return <div key={msg.id} className="self-end max-w-[82%] bg-secondary text-foreground text-xs px-3.5 py-2.5 rounded-2xl rounded-br-sm leading-relaxed">{msg.text}</div>;
        })}
        {isTyping && <div className="self-start bg-primary/15 px-4 py-3 rounded-2xl rounded-bl-sm"><div className="flex gap-1.5">{[0,150,300].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div></div>}
      </div>
      {endData ? (
        <div className="px-4 py-4 bg-card border-t border-border">
          <div className="text-center mb-3">
            <div className="text-2xl mb-1">{endData.status === "D1" ? "📅" : "📋"}</div>
            <p className="text-foreground font-medium text-sm">{endData.status === "D1" ? "Meeting booked!" : "Call ended"}</p>
            <p className="text-muted-foreground text-xs mt-1 leading-relaxed">{endData.status === "D1" ? "n8n fires: Google Calendar + CRM updated." : "n8n fires: CRM marks lead as lost."}</p>
          </div>
          <div className="bg-background rounded-xl border border-border p-3 mb-3 font-mono text-[11px]">
            <p className="text-muted-foreground mb-1.5 font-sans text-[10px]">payload → n8n webhook</p>
            {[["lead_name","John Smith"],["outcome_status", endData.status === "D1" ? "D1 — Meeting booked" : "D3 — Qualified discard"],[...(endData.status==="D1"?[["datetime",getNextWeekISO()]]:[])],...(endData.status==="D1"?[]:[[]])  ,["team_size", endData.state.teamSize||"—"],["main_pain", endData.state.pain||"—"],["current_tool", endData.state.competitor||"—"],["channel","phone call"]].filter(r => r.length===2).map(([k,v])=>(
              <div key={k as string} className="flex gap-2 mb-1"><span className="text-muted-foreground min-w-[110px]">{k as string}</span><span className={k==="outcome_status"?(endData.status==="D1"?"text-primary":"text-destructive"):"text-foreground"}>{v as string}</span></div>
            ))}
          </div>
          <button onClick={restart} className="w-full bg-primary hover:bg-primary/90 active:scale-95 text-primary-foreground text-xs font-medium py-2.5 rounded-xl transition-all">↻ Run simulation again</button>
        </div>
      ) : (
        <div className="px-4 py-3 bg-card border-t border-border min-h-[72px]">
          {messages.length === 0 && !isTyping ? (
            <button onClick={startFlow} className="w-full bg-primary hover:bg-primary/90 active:scale-95 text-primary-foreground font-medium text-xs py-3 rounded-xl transition-all">📞 Start call simulation</button>
          ) : options.length > 0 ? (
            <><p className="text-muted-foreground text-[10px] mb-1.5">Reply as the lead:</p><div className="flex flex-col gap-1.5">{options.map((opt, i) => (<button key={i} onClick={opt.action} className={`text-left text-xs px-3 py-2 rounded-xl border transition-all active:scale-[0.98] leading-snug min-h-[44px] ${opt.type==="positive"?"border-primary/50 text-primary hover:bg-primary/10":opt.type==="objection"?"border-destructive/50 text-destructive hover:bg-destructive/10":"border-border text-muted-foreground hover:bg-secondary"}`}>{opt.label}</button>))}</div></>
          ) : <div className="flex items-center justify-center h-10"><span className="text-muted-foreground text-xs">waiting...</span></div>}
        </div>
      )}
    </div>
  );
}

// ─── CASE PAGE (English) ──────────────────────────────────────────────────────
export default function VozisCaseEN() {
  const navigate = useNavigate();

  const metrics = [
    { value: "300%", label: "more contacts/day", sub: "without growing the team" },
    { value: "↓40%", label: "time on repetitive tasks", sub: "per sales rep" },
    { value: "3×", label: "scaling capacity", sub: "within 30 days of use" },
    { value: "24/7", label: "active prospecting", sub: "uninterrupted" },
  ];

  const timeline = [
    { phase: "Week 1", title: "Integration & training", desc: "Vozis connects to the CRM and learns the company's sales script. Zero code — conversational configuration." },
    { phase: "Week 2–3", title: "Live pilot", desc: "The AI starts real calls. Conversion data feeds the control panel in real time." },
    { phase: "Week 4+", title: "Continuous scale", desc: "Contact volume grows automatically. The human team focuses 100% on closing the hot leads AI generates." },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* HERO */}
      <section className="gradient-hero pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="mb-8 hover:bg-primary/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to projects
          </Button>

          <div className="flex items-center gap-2 mb-6">
            <Badge variant="secondary" className="bg-primary/10 text-primary">Sales Automation</Badge>
            <Badge variant="secondary" className="bg-primary/10 text-primary">Voice AI</Badge>
            <span className="text-xs text-muted-foreground">2024</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
            How Vozis multiplies{" "}
            <span className="text-primary">qualified outreach</span> without growing the team
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            An AI platform that takes over prospecting, qualification, and follow-up calls — freeing the human team to close. This case documents the system, conversational design decisions, and projected metrics.
          </p>
        </div>
      </section>

      {/* METRICS STRIP */}
      <section className="bg-secondary py-10 border-y border-border">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {metrics.map((m) => (
            <div key={m.value} className="text-center">
              <p className="text-3xl font-bold text-primary mb-1">{m.value}</p>
              <p className="text-xs text-foreground font-medium">{m.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{m.sub}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6">

        {/* CONTEXT */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">01 · Context</p>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-4">The bottleneck every sales team knows</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Sales teams spend up to <strong className="text-foreground">60% of their time</strong> on tasks that don't directly generate revenue: cold calling, follow-ups, lead qualification, and CRM updates. The result is predictable — your best closers are too busy to actually close.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Vozis was designed to flip that equation. The AI takes over the top of funnel with human-like naturalness, while the human team operates exclusively in moments that require relational intelligence.
              </p>
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border">
              <p className="text-xs text-muted-foreground mb-4 font-medium">Current time distribution (typical)</p>
              {[
                { label: "Cold calls & follow-up", pct: 58, color: "bg-destructive/70" },
                { label: "Manual qualification", pct: 18, color: "bg-yellow-500/70" },
                { label: "Negotiation & closing", pct: 16, color: "bg-primary" },
                { label: "CRM updates", pct: 8, color: "bg-muted-foreground/50" },
              ].map((b) => (
                <div key={b.label} className="mb-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{b.label}</span><span className="font-medium">{b.pct}%</span></div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full ${b.color} rounded-full`} style={{ width: `${b.pct}%` }} /></div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground mt-4">With Vozis: human team focuses on the 16% that generates revenue</p>
            </div>
          </div>
        </section>

        {/* SOLUTION */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">02 · The Solution</p>
          <h2 className="text-2xl font-bold text-foreground mb-8">Conversational design that sounds human</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: "🎯", title: "Intelligent script", desc: "The AI follows a structured script with natural detours for objections, pauses, and active listening — not a rigid set of lines." },
              { icon: "🔗", title: "Native integration", desc: "Connects with HubSpot, Pipedrive, and Google Calendar via n8n. When the call ends, the CRM is already updated." },
              { icon: "📊", title: "Qualification as byproduct", desc: "Each call captures team size, main pain, and current competitor — feeding the funnel automatically without any manual entry." },
            ].map((card) => (
              <div key={card.title} className="border border-border rounded-2xl p-5 hover:border-primary/40 hover:bg-primary/5 transition-all">
                <div className="text-2xl mb-3">{card.icon}</div>
                <h3 className="font-medium text-foreground mb-2 text-sm">{card.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* N8N Architecture */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <p className="text-xs text-muted-foreground mb-5 font-medium">Orchestration architecture · n8n</p>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {[
                { label: "Call ends", color: "bg-secondary text-foreground" },
                { label: "n8n Webhook", color: "bg-secondary text-foreground" },
                { label: "Switch D1/D2/D3", color: "bg-yellow-500/20 text-yellow-400" },
                { label: "D1 → Calendar + CRM", color: "bg-primary/20 text-primary" },
                { label: "D3 → CRM Lost", color: "bg-destructive/20 text-destructive" },
                { label: "Sheets Dashboard", color: "bg-secondary text-foreground" },
              ].map((node, i, arr) => (
                <div key={node.label} className="flex items-center gap-2 flex-shrink-0">
                  <div className={`${node.color} text-xs px-3 py-2 rounded-xl text-center min-w-[100px]`}>{node.label}</div>
                  {i < arr.length - 1 && <span className="text-muted-foreground text-xs">→</span>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROTOTYPE */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">03 · Interactive prototype</p>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Try it yourself</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This prototype replicates Vozis's full sales script. You play the lead — respond positively or raise real objections like "we already use another tool" or "what's the price?".
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                When the call ends, the system shows the exact <strong className="text-foreground">JSON payload</strong> that would fire to n8n — already populated with qualification data collected during the conversation.
              </p>
              <div className="space-y-3">
                {["Test the objection-handling flow", "See D1 (booked) or D3 (discarded)", "Inspect the generated payload"].map((tip) => (
                  <div key={tip} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {tip}
                  </div>
                ))}
              </div>
            </div>
            <VozisPrototypeEN />
          </div>
        </section>

        {/* IMPLEMENTATION */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">04 · Implementation</p>
          <h2 className="text-2xl font-bold text-foreground mb-8">From setup to scale in 30 days</h2>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div key={item.phase} className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-background border-2 border-primary flex items-center justify-center text-primary font-medium text-xs flex-shrink-0">{i + 1}</div>
                  {i < timeline.length - 1 && <div className="w-px flex-1 bg-border my-2" />}
                </div>
                <div className="pb-8">
                  <p className="text-xs text-primary font-medium mb-1">{item.phase}</p>
                  <h3 className="font-medium text-foreground mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PROJECTIONS */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">05 · Impact projections</p>
          <h2 className="text-2xl font-bold text-foreground mb-8">What the numbers say</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-5">
              {[
                { before: "80 calls/day", after: "320 calls/day", label: "Outreach volume", icon: "📞" },
                { before: "~6h manual tasks/rep", after: "~2h", label: "Time on repetitive work", icon: "⏱" },
                { before: "Business hours only", after: "24 hours / 7 days", label: "Prospecting window", icon: "🌐" },
                { before: "Inconsistent qualification data", after: "100% structured in CRM", label: "Data quality", icon: "📊" },
              ].map((row) => (
                <div key={row.label} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">{row.icon}</span>
                    <p className="text-xs text-muted-foreground font-medium">{row.label}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground line-through">{row.before}</span>
                    <span className="text-muted-foreground text-xs">→</span>
                    <span className="text-sm font-medium text-primary">{row.after}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border flex flex-col justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-6">Projected ROI · 90 days</p>
                <div className="text-5xl font-bold text-primary mb-2">3–5×</div>
                <p className="text-foreground text-sm mb-4">projected return on investment</p>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Based on: 300% increase in contact volume, 40% reduction in operational time per rep, and maintained average conversion rate — without additional headcount.
                </p>
              </div>
              <div className="mt-6 border-t border-border pt-4">
                <p className="text-xs text-muted-foreground">* Projections based on benchmarks from AI-powered commercial automation platforms in 5–50 person companies</p>
              </div>
            </div>
          </div>
        </section>

        {/* DESIGN REFLECTIONS */}
        <section className="py-14">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">06 · Design reflections</p>
          <h2 className="text-2xl font-bold text-foreground mb-6">What I learned designing this system</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Naturalness > Efficiency", text: "The temptation is to optimize for speed. But rigid conversations increase early hang-ups. Pauses, connectors, and emotional validation convert more than direct scripts." },
              { title: "Objection isn't rejection", text: "Each objection ('what's the price?', 'we use another tool') signals interest. The script design treats objections as forks, not dead ends." },
              { title: "Data as a byproduct", text: "Structured qualification (team size, pain, competitor) happens naturally in the conversation. The CRM is updated without any human needing to type anything." },
              { title: "Asymmetric scalability", text: "AI scales linearly — doubling volume doesn't cost twice as much. This structurally changes the math of commercial growth, not just incrementally." },
            ].map((item) => (
              <div key={item.title} className="border-l-2 border-primary pl-5">
                <h3 className="font-medium text-foreground mb-2 text-sm">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
